import { initI18n } from '@frontend/core/i18n';
import { useAppTypeStore } from '@frontend/core/store';
import { reloadOnChunkLoadError } from '@frontend/core/utils';
import { type ComponentType, StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import '@frontend/core/index.css';

const APP_TYPES = ['UNIVERSAL', 'FOCUS'] as const;

type AppType = (typeof APP_TYPES)[number];

interface AppTypeInfo {
  type: AppType;
  tenant?: string;
}

const APP_TYPE_TIMEOUT_MS = 10_000;
const FALLBACK_APP_TYPE: AppType = 'UNIVERSAL';

const APP_LOADERS: Record<AppType, () => Promise<ComponentType>> = {
  UNIVERSAL: () => import('@frontend/universal').then((module) => module.App),
  FOCUS: () => import('@frontend/focus').then((module) => module.App),
};

function isKnownAppType(value: unknown): value is AppType {
  return APP_TYPES.includes(value as AppType);
}

function resolveAppDomain(): string {
  if (import.meta.env.MODE === 'development' && import.meta.env.PROXY_TARGET_URL) {
    return new URL(import.meta.env.PROXY_TARGET_URL).hostname;
  }
  return window.location.hostname;
}

async function requestAppType(domain: string): Promise<AppTypeInfo> {
  const url = `/api/public/app-type?domain=${encodeURIComponent(domain)}`;
  const response = await fetch(url, { signal: AbortSignal.timeout(APP_TYPE_TIMEOUT_MS) });

  if (!response.ok) {
    throw new Error(`App type request failed with status ${response.status}`);
  }

  const payload = (await response.json()) as { data?: { type?: unknown; tenant?: string } };
  const type = payload.data?.type;

  if (!isKnownAppType(type)) {
    throw new Error(`App type request returned unknown type: ${String(type)}`);
  }

  return { type, tenant: payload.data?.tenant };
}

async function fetchAppType(domain: string): Promise<AppTypeInfo> {
  try {
    return await requestAppType(domain);
  } catch (error) {
    console.warn(`Falling back to app type "${FALLBACK_APP_TYPE}"`, error);
    return { type: FALLBACK_APP_TYPE, tenant: 'innomos' };
  }
}

initI18n();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

const reactRoot = ReactDOM.createRoot(rootElement);

window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnChunkLoadError(event.payload)) {
    event.preventDefault();
  }
});

async function bootstrap() {
  const appTypeInfo = await fetchAppType(resolveAppDomain());
  // Publish the resolved app type so core (useAppType) reads it instead of
  // re-fetching GET /public/app-type.
  useAppTypeStore.getState().setAppType(appTypeInfo);
  const App = await APP_LOADERS[appTypeInfo.type]();

  reactRoot.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

bootstrap().catch((error) => {
  console.error('Failed to bootstrap application', error);
  if (reloadOnChunkLoadError(error)) {
    return;
  }
  reactRoot.render(
    <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <p>Failed to load application. Please try again later.</p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
});
